import { Navigation, PartyPopper } from 'lucide-react'
import type { CaptureMode, CaptureProgress } from '@/lib/capture'
import type { Guidance } from '@/hooks/useCaptureSession'
import { cn } from '@/lib/utils'

interface GuidanceCardProps {
  mode: CaptureMode
  progress: CaptureProgress | null
  guidance: Guidance | null
  /** True when the surveyor device has no current fix to steer from. */
  waitingForFix: boolean
}

/**
 * The heads-up card over the map: where to go next, and how far.
 *
 * This is the piece that answers "what do I capture, and from where" without the
 * operator having to read a list — distance and a compass arrow, big enough to
 * glance at while walking.
 */
export function GuidanceCard({ mode, progress, guidance, waitingForFix }: GuidanceCardProps) {
  const complete = progress !== null && progress.total > 0 && progress.done === progress.total

  return (
    <div className="pointer-events-none absolute inset-x-0 top-4 z-10 flex justify-center px-4">
      <div className="bg-card/90 pointer-events-auto flex items-center gap-4 rounded-xl border px-4 py-3 shadow-lg backdrop-blur-md">
        {complete ? (
          <>
            <PartyPopper className="text-success size-5 shrink-0" />
            <div>
              <p className="text-sm font-medium">This floor is fully captured.</p>
              <p className="text-muted-foreground text-xs">
                {progress.total} {mode.unit}s done — switch floor to carry on.
              </p>
            </div>
          </>
        ) : guidance ? (
          <>
            <div
              className="text-primary shrink-0 transition-transform duration-300"
              style={{ transform: `rotate(${guidance.bearingDegrees}deg)` }}
              aria-hidden
            >
              <Navigation className="size-7 fill-current" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium">
                Next {mode.unit}:{' '}
                <span className="tabular-nums">{guidance.distanceMeters.toFixed(1)} m</span>{' '}
                <span className="text-muted-foreground font-normal">{guidance.compass}</span>
              </p>
              <p className="text-muted-foreground text-xs">{mode.hint}</p>
            </div>
          </>
        ) : (
          <div>
            <p className="text-sm font-medium">
              {waitingForFix ? 'Waiting for a position fix…' : 'Nothing to capture on this floor.'}
            </p>
            <p className="text-muted-foreground text-xs">
              {waitingForFix
                ? 'Pick the surveyor’s device in the sidebar.'
                : 'The floor has no geometry to lay a grid over.'}
            </p>
          </div>
        )}

        {progress !== null && progress.total > 0 && (
          <div className="border-border ml-2 shrink-0 border-l pl-4 text-right">
            <p className="text-sm font-medium tabular-nums">
              {progress.done}
              <span className="text-muted-foreground font-normal">/{progress.total}</span>
            </p>
            <div className="bg-muted mt-1 h-1 w-20 overflow-hidden rounded-full">
              <div
                className={cn('h-full rounded-full transition-[width] duration-500', 'bg-success')}
                style={{ width: `${Math.round(progress.ratio * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
